import {
  DIAGNOSIS_CRITICALITIES,
  DIAGNOSIS_CRITICALITY_LABELS,
  DIAGNOSIS_EDITABLE_STATUSES,
  DIAGNOSIS_STATUS_LABELS,
  DIAGNOSIS_STATUSES,
  DIAGNOSIS_TO_MATRIX_FROM,
  diagnosisInputSchema,
  diagnosisItemSchema,
  diagnosisListSchema,
  diagnosisOptionsSchema,
  type DiagnosisCriticalityName,
  type DiagnosisInput,
  type DiagnosisItem,
  type DiagnosisOptions,
  type DiagnosisStatusName,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClipboardList,
  Eye,
  FolderKanban,
  Grid3x3,
  ListChecks,
  Lock,
  Pencil,
  Plus,
  Rocket,
  Search,
  UserRound,
} from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { CollaborationPanel } from '@/features/collaboration/CollaborationPanel';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';
import {
  CRITICALITY_LIGHT,
  ItemSummary,
  RelateObjective,
  SCALE,
  SCALE_LABELS,
  STATUS_LIGHT,
  TransformInitiativeDialog,
} from '@/features/diagnosis/ItemParts';

const PAGE_SIZE = 30;

const editableStatus = (s: DiagnosisStatusName) =>
  (DIAGNOSIS_EDITABLE_STATUSES as readonly DiagnosisStatusName[]).includes(s);

const textareaClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring aria-invalid:border-danger';

/**
 * Diagnóstico › Diagnóstico empresarial (T-160b, RF-10, RF-11, PRD §11.4; DEC-061): problemas, lacunas, necessidades
 * e evidências levantados no diagnóstico, com filtros, cadastro e as ações de cada item.
 */
export function DiagnosticoPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Diagnóstico empresarial"
        description="Problemas, lacunas, necessidades e evidências levantados no diagnóstico — o ponto de partida da matriz de priorização e da estratégia."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O diagnóstico pertence a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <DiagnosisSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function DiagnosisSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  /** Projeto arquivado: só consulta. */
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/diagnosis`;
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [criticality, setCriticality] = useState<DiagnosisCriticalityName | ''>('');
  const [status, setStatus] = useState<DiagnosisStatusName | ''>('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<DiagnosisItem | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const detailId = params.get('item');
  const ids = {
    search: useId(),
    department: useId(),
    unit: useId(),
    criticality: useId(),
    status: useId(),
  };

  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q]);

  const options = useQuery({
    queryKey: ['diagnosis', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: diagnosisOptionsSchema, signal }),
  });
  const filters = { q, departmentId, businessUnitId, criticality, status, page };
  const list = useQuery({
    queryKey: ['diagnosis', projectId, 'list', filters],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: {
          q: q || undefined,
          departmentId: departmentId || undefined,
          businessUnitId: businessUnitId || undefined,
          criticality: criticality || undefined,
          status: status || undefined,
          page,
          pageSize: PAGE_SIZE,
        },
        schema: diagnosisListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  // O item aparece na origem dos planos e das iniciativas: atualizam junto.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['diagnosis', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['action-plans', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['initiatives', projectId] }),
    ]);

  const openDetail = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('item', id);
        else next.delete('item');
        return next;
      },
      { replace: true },
    );

  async function save(input: DiagnosisInput) {
    const target = editing === 'new' ? null : editing;
    const saved = await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PUT' : 'POST',
      body: input,
      schema: diagnosisItemSchema,
    });
    setEditing(null);
    toast.success(target ? 'Item salvo' : 'Item registrado', { description: saved.title });
    await refresh();
  }

  const filtered = Boolean(q || departmentId || businessUnitId || criticality || status);
  const opts = options.data;
  const clearFilters = () => {
    setSearch('');
    setDepartmentId('');
    setBusinessUnitId('');
    setCriticality('');
    setStatus('');
    setPage(1);
  };

  return (
    <div className="mt-6 flex flex-col gap-5">
      {readOnly ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Projeto arquivado: o diagnóstico fica disponível só para consulta.
        </p>
      ) : null}
      {options.isError ? (
        <FormAlert tone="error">
          <span className="flex flex-wrap items-center gap-2">
            Não foi possível carregar as áreas, unidades e pessoas (filtros e cadastro):{' '}
            {errorMessage(options.error)}
            <Button
              variant="outline"
              size="sm"
              loading={options.isFetching}
              onClick={() => void options.refetch()}
            >
              Tentar de novo
            </Button>
          </span>
        </FormAlert>
      ) : null}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <label htmlFor={ids.search} className="sr-only">
              Buscar item de diagnóstico
            </label>
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id={ids.search}
              type="search"
              placeholder="Buscar pelo título ou pela descrição"
              value={search}
              maxLength={100}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-9"
            />
          </div>
          {can('diagnostico', 'create') ? (
            <Button
              className="h-11"
              onClick={() => setEditing('new')}
              disabled={!opts || opts.departments.length === 0}
            >
              <Plus aria-hidden />
              Novo item
            </Button>
          ) : null}
        </div>
        {can('diagnostico', 'create') && opts && opts.departments.length === 0 ? (
          <p className="text-sm text-muted-foreground" role="note">
            Para registrar itens, cadastre antes as áreas da empresa: cada item pertence a uma área.
          </p>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Filter
            id={ids.department}
            label="Área"
            value={departmentId}
            onChange={(v) => {
              setDepartmentId(v);
              setPage(1);
            }}
          >
            <option value="">Todas as áreas</option>
            {opts?.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.unit}
            label="Unidade"
            value={businessUnitId}
            onChange={(v) => {
              setBusinessUnitId(v);
              setPage(1);
            }}
          >
            <option value="">Todas as unidades</option>
            {opts?.businessUnits.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.criticality}
            label="Criticidade"
            value={criticality}
            onChange={(v) => {
              setCriticality(v as DiagnosisCriticalityName | '');
              setPage(1);
            }}
          >
            <option value="">Todas as criticidades</option>
            {DIAGNOSIS_CRITICALITIES.map((c) => (
              <option key={c} value={c}>
                {DIAGNOSIS_CRITICALITY_LABELS[c]}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.status}
            label="Status"
            value={status}
            onChange={(v) => {
              setStatus(v as DiagnosisStatusName | '');
              setPage(1);
            }}
          >
            <option value="">Todos (menos arquivados)</option>
            {DIAGNOSIS_STATUSES.map((s) => (
              <option key={s} value={s}>
                {DIAGNOSIS_STATUS_LABELS[s]}
              </option>
            ))}
          </Filter>
        </div>
      </div>

      {list.isPending ? (
        <LoadingState rows={5} label="Carregando o diagnóstico" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : list.data.total === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={ClipboardList}
          title={filtered ? 'Nenhum item encontrado' : 'Nenhum item de diagnóstico ainda'}
          description={
            filtered
              ? 'Nenhum item corresponde à busca e aos filtros escolhidos.'
              : 'Registre os problemas, lacunas e necessidades levantados: área, impacto, urgência, esforço, criticidade e evidências.'
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clearFilters}>
                Limpar filtros
              </Button>
            ) : can('diagnostico', 'create') && opts && opts.departments.length > 0 ? (
              <Button onClick={() => setEditing('new')}>
                <Plus aria-hidden />
                Novo item
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="flex flex-col gap-3" aria-busy={list.isFetching}>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {list.data.total === 1 ? '1 item' : `${list.data.total} itens`}
          </p>
          <ul aria-label="Itens do diagnóstico" className="flex flex-col gap-2">
            {list.data.items.map((d) => (
              <li key={d.id} className="rounded-lg border border-border bg-surface p-4">
                <article aria-label={d.title} className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold break-words">{d.title}</h2>
                      <StatusBadge
                        status={CRITICALITY_LIGHT[d.criticality]}
                        label={`Criticidade ${DIAGNOSIS_CRITICALITY_LABELS[d.criticality].toLowerCase()}`}
                      />
                      <StatusBadge
                        status={STATUS_LIGHT[d.status]}
                        label={DIAGNOSIS_STATUS_LABELS[d.status]}
                      />
                    </div>
                    <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <Meta
                        label="Área"
                        value={`${d.department.name}${d.businessUnit ? ` · ${d.businessUnit.name}` : ''}`}
                      />
                      <Meta
                        label="Impacto, urgência e esforço"
                        value={`Impacto ${d.impact} · Urgência ${d.urgency} · Esforço ${d.effort}`}
                      />
                      <Meta label="Levantado por" value={d.surveyedBy?.name ?? 'Não informado'} />
                    </dl>
                  </div>
                  <div className="flex gap-1 sm:shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openDetail(d.id)}>
                      <Eye aria-hidden />
                      Abrir <span className="sr-only">{d.title}</span>
                    </Button>
                    {can('diagnostico', 'edit') && d.status !== 'ARCHIVED' ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Editar ${d.title}`}
                        onClick={() => setEditing(d)}
                        disabled={!opts}
                      >
                        <Pencil aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                </article>
              </li>
            ))}
          </ul>
          {list.data.totalPages > 1 ? (
            <nav aria-label="Paginação do diagnóstico" className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Página {list.data.page} de {list.data.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= list.data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </nav>
          ) : null}
        </div>
      )}

      <DiagnosisDetail
        base={base}
        organizationId={organizationId}
        projectId={projectId}
        itemId={detailId}
        options={opts}
        readOnly={readOnly}
        onClose={() => openDetail(null)}
        onEdit={
          can('diagnostico', 'edit') && opts
            ? (d) => {
                openDetail(null);
                setEditing(d);
              }
            : undefined
        }
        onChanged={refresh}
      />
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Novo item de diagnóstico' : 'Editar item de diagnóstico'}
        className="w-[min(46rem,calc(100vw-2rem))]"
      >
        {editing !== null && opts ? (
          <DiagnosisForm
            initial={editing === 'new' ? undefined : editing}
            options={opts}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function DiagnosisDetail({
  base,
  organizationId,
  projectId,
  itemId,
  options,
  readOnly,
  onClose,
  onEdit,
  onChanged,
}: {
  base: string;
  organizationId: string;
  projectId: string;
  itemId: string | null;
  options: DiagnosisOptions | undefined;
  readOnly: boolean;
  onClose: () => void;
  onEdit?: (d: DiagnosisItem) => void;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const [transforming, setTransforming] = useState(false);
  const detail = useQuery({
    queryKey: ['diagnosis', projectId, 'detail', itemId],
    queryFn: ({ signal }) => client.request(`${base}/${itemId}`, { schema: diagnosisItemSchema, signal }),
    enabled: itemId !== null,
  });
  const d = detail.data;
  const sendToMatrix = useMutation({
    meta: { handlesError: true },
    mutationFn: (id: string) =>
      client.request(`${base}/${id}/matrix`, { method: 'POST', schema: diagnosisItemSchema }),
    onSuccess: async (saved) => {
      toast.success('Item enviado para a matriz de priorização', { description: saved.title });
      await onChanged();
    },
    onError: (err) =>
      toast.error('Não foi possível enviar para a matriz', { description: errorMessage(err) }),
  });

  const canEdit = Boolean(d) && can('diagnostico', 'edit') && d?.status !== 'ARCHIVED';
  return (
    <>
      <Dialog
        open={itemId !== null && !transforming}
        onOpenChange={(open) => !open && onClose()}
        title={d?.title ?? 'Item de diagnóstico'}
        className="w-[min(46rem,calc(100vw-2rem))]"
      >
        {detail.isPending ? (
          <LoadingState rows={4} label="Carregando o item" />
        ) : detail.isError ? (
          <ErrorState
            error={detail.error}
            onRetry={() => void detail.refetch()}
            retrying={detail.isFetching}
          />
        ) : d ? (
          <div className="flex flex-col gap-5 text-sm">
            <ItemSummary item={d} organizationId={organizationId} projectId={projectId} />
            {canEdit && canDo('objetivos', 'view') ? (
              <RelateObjective
                base={base}
                item={d}
                objectives={options?.objectives ?? []}
                onChanged={onChanged}
              />
            ) : null}
            {canEdit ? (
              <section aria-label="Ações do item" className="flex flex-col gap-2">
                <h3 className="font-bold">Ações</h3>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  {DIAGNOSIS_TO_MATRIX_FROM.includes(d.status) ? (
                    <Button
                      variant="outline"
                      loading={sendToMatrix.isPending}
                      onClick={() => sendToMatrix.mutate(d.id)}
                    >
                      <Grid3x3 aria-hidden />
                      Enviar para a matriz
                    </Button>
                  ) : null}
                  {can('iniciativas', 'create') && !d.initiative ? (
                    <Button variant="outline" onClick={() => setTransforming(true)}>
                      <Rocket aria-hidden />
                      Transformar em iniciativa
                    </Button>
                  ) : null}
                  {can('planos-acao', 'create') ? (
                    <Button asChild variant="outline">
                      <Link
                        to={`/empresas/${organizationId}/execucao/planos-acao?projeto=${projectId}&diagnostico=${d.id}`}
                      >
                        <ListChecks aria-hidden />
                        Gerar plano de ação
                      </Link>
                    </Button>
                  ) : null}
                  {onEdit ? (
                    <Button variant="outline" onClick={() => onEdit(d)}>
                      <Pencil aria-hidden />
                      Editar item
                    </Button>
                  ) : null}
                </div>
              </section>
            ) : null}
            <CollaborationPanel
              organizationId={organizationId}
              projectId={projectId}
              subject="diagnosis"
              subjectId={d.id}
              label={d.title}
            />
          </div>
        ) : null}
      </Dialog>
      <TransformInitiativeDialog
        organizationId={organizationId}
        projectId={projectId}
        base={base}
        item={d}
        open={transforming}
        onClose={() => setTransforming(false)}
        onDone={onChanged}
      />
    </>
  );
}

function DiagnosisForm({
  initial,
  options,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: DiagnosisItem;
  options: DiagnosisOptions;
  onSubmit: (input: DiagnosisInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [departmentId, setDepartmentId] = useState(initial?.department.id ?? '');
  const [businessUnitId, setBusinessUnitId] = useState(initial?.businessUnit?.id ?? '');
  const [impact, setImpact] = useState(initial ? String(initial.impact) : '');
  const [urgency, setUrgency] = useState(initial ? String(initial.urgency) : '');
  const [effort, setEffort] = useState(initial ? String(initial.effort) : '');
  const [criticality, setCriticality] = useState<DiagnosisCriticalityName | ''>(initial?.criticality ?? '');
  const [surveyedById, setSurveyedById] = useState(initial?.surveyedBy?.id ?? '');
  const [evidence, setEvidence] = useState(initial?.evidence ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const statusLocked = initial !== undefined && !editableStatus(initial.status);
  const [status, setStatus] = useState<DiagnosisStatusName>(initial?.status ?? 'IDENTIFIED');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = {
    title: useId(),
    description: useId(),
    department: useId(),
    unit: useId(),
    impact: useId(),
    urgency: useId(),
    effort: useId(),
    criticality: useId(),
    surveyor: useId(),
    evidence: useId(),
    notes: useId(),
    status: useId(),
  };
  // Área, unidade ou pessoa atual que saiu das opções ativas continua escolhível (salvar não troca).
  const keep = (
    list: { id: string; name: string }[],
    current: { id: string; name: string } | null | undefined,
  ) => (current && !list.some((x) => x.id === current.id) ? [...list, current] : list);
  const departments = keep(options.departments, initial?.department);
  const units = keep(options.businessUnits, initial?.businessUnit);
  const people = keep(options.people, initial?.surveyedBy);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const num = (v: string) => (v === '' ? undefined : Number(v));
    const parsed = diagnosisInputSchema.safeParse({
      title,
      description,
      departmentId: departmentId || undefined,
      businessUnitId: businessUnitId || null,
      impact: num(impact),
      urgency: num(urgency),
      effort: num(effort),
      criticality: criticality || undefined,
      surveyedById: surveyedById || null,
      evidence,
      notes,
      // Depois da matriz o status segue as ações: vai o atual, e a API o mantém.
      status: statusLocked ? 'IN_ANALYSIS' : status,
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  const scaleSelect = (
    id: string,
    label: string,
    value: string,
    set: (v: string) => void,
    error: string | undefined,
  ) => (
    <FormField id={id} label={label} error={error}>
      <NativeSelect {...fieldA11y(id, error)} value={value} onChange={(e) => set(e.target.value)}>
        <option value="">Escolha de 1 a 5</option>
        {SCALE.map((n) => (
          <option key={n} value={n}>
            {SCALE_LABELS[n]}
          </option>
        ))}
      </NativeSelect>
    </FormField>
  );

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.title} label="Título do problema" error={errors.title}>
        <Input
          {...fieldA11y(ids.title, errors.title)}
          value={title}
          maxLength={160}
          placeholder="Ex.: Ausência de gerente geral"
          onChange={(e) => setTitle(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.description} label="Descrição (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          rows={3}
          maxLength={4000}
          onChange={(e) => setDescription(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.department} label="Área" error={errors.departmentId}>
          <NativeSelect
            {...fieldA11y(ids.department, errors.departmentId)}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
          >
            <option value="">Escolha a área</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.unit} label="Unidade (opcional)" error={errors.businessUnitId}>
          <NativeSelect
            {...fieldA11y(ids.unit, errors.businessUnitId)}
            value={businessUnitId}
            onChange={(e) => setBusinessUnitId(e.target.value)}
          >
            <option value="">Sem unidade</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {scaleSelect(ids.impact, 'Impacto', impact, setImpact, errors.impact)}
        {scaleSelect(ids.urgency, 'Urgência', urgency, setUrgency, errors.urgency)}
        {scaleSelect(ids.effort, 'Esforço estimado', effort, setEffort, errors.effort)}
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <FormField id={ids.criticality} label="Criticidade" error={errors.criticality}>
          <NativeSelect
            {...fieldA11y(ids.criticality, errors.criticality)}
            value={criticality}
            onChange={(e) => setCriticality(e.target.value as DiagnosisCriticalityName | '')}
          >
            <option value="">Escolha a criticidade</option>
            {DIAGNOSIS_CRITICALITIES.map((c) => (
              <option key={c} value={c}>
                {DIAGNOSIS_CRITICALITY_LABELS[c]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.surveyor} label="Responsável pelo levantamento" error={errors.surveyedById}>
          <NativeSelect
            {...fieldA11y(ids.surveyor, errors.surveyedById)}
            value={surveyedById}
            onChange={(e) => setSurveyedById(e.target.value)}
          >
            <option value="">Não informado</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField
          id={ids.status}
          label="Status"
          error={errors.status}
          hint={
            statusLocked ? (
              <p className="text-xs text-muted-foreground">
                O item já saiu da análise: o status agora segue a matriz e o tratamento.
              </p>
            ) : undefined
          }
        >
          <NativeSelect
            {...fieldA11y(ids.status, errors.status, statusLocked)}
            value={status}
            disabled={statusLocked}
            onChange={(e) => setStatus(e.target.value as DiagnosisStatusName)}
          >
            {(statusLocked ? [status] : DIAGNOSIS_EDITABLE_STATUSES).map((s) => (
              <option key={s} value={s}>
                {DIAGNOSIS_STATUS_LABELS[s]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      <FormField id={ids.evidence} label="Evidência (opcional)" error={errors.evidence}>
        <textarea
          {...fieldA11y(ids.evidence, errors.evidence)}
          value={evidence}
          rows={2}
          maxLength={4000}
          placeholder="O que mostra o problema: entrevistas, números, documentos. Fotos e documentos entram nos anexos do item."
          onChange={(e) => setEvidence(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      <FormField id={ids.notes} label="Observações (opcional)" error={errors.notes}>
        <textarea
          {...fieldA11y(ids.notes, errors.notes)}
          value={notes}
          rows={2}
          maxLength={4000}
          onChange={(e) => setNotes(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar item' : 'Registrar item'}
        </Button>
      </div>
    </form>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-center gap-1">
      <dt className="sr-only">{label}</dt>
      {label === 'Levantado por' ? <UserRound className="size-3.5 shrink-0" aria-hidden /> : null}
      <dd className="break-words">{value}</dd>
    </div>
  );
}

function Filter({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </NativeSelect>
    </div>
  );
}
